import type { Command } from '@tiptap/core';
import type { Fragment as PMFragment, Node as PMNode } from '@tiptap/pm/model';
import { getActiveBlock, findParentRow, isLastChildOf, selectAtBlockStart } from './rowUtils';
import { ELEMENT_ROW_NODE_NAME } from '../constants';

export function unwrapLikeWord(): Command {
  return ({ state, dispatch }) => {
    if (!dispatch) {
      console.error('[unwrapLikeWord] No dispatch function available');
      return false;
    }
    const rowType = state.schema.nodes[ELEMENT_ROW_NODE_NAME];
    const info = getActiveBlock(state);
    if (!info) return false;

    const { pos, node } = info;
    const rowInfo = findParentRow(state, pos);
    if (!rowInfo) return false;

    // Not available on the last element
    if (isLastChildOf(rowInfo.node, pos, rowInfo.pos)) return false;

    let tr = state.tr;

    // Row bounds
    const rowStart = rowInfo.pos + 1;
    const rowEnd = rowInfo.pos + rowInfo.node.nodeSize - 1;

    // Split after the selected block
    const afterSelected = pos + node.nodeSize;

    const leftContent = tr.doc.slice(rowStart, afterSelected).content; // includes selected
    const rightContent = tr.doc.slice(afterSelected, rowEnd).content; // ≥ 1 child (since not last)

    const leftCount = leftContent.childCount;
    const rightCount = rightContent.childCount;

    const isFirst = leftCount === 1;

    const buildRow = (content: PMFragment) => rowType.create({ ...rowInfo.node.attrs }, content);
    const copyNode = (n: PMNode) => n.copy(n.content);

    if (isFirst) {
      // --- Selected is the first element: dissolve the row ---
      const selectedStandalone = copyNode(node);

      // Replace entire row with the selected standalone block
      tr = tr.replaceRangeWith(rowInfo.pos, rowInfo.pos + rowInfo.node.nodeSize, selectedStandalone);
      const selectedPos = tr.mapping.map(rowInfo.pos); // absolute start of the inserted selected block
      const insertPos = selectedPos + selectedStandalone.nodeSize;

      if (rightCount === 1) {
        // Insert the single right block after the selected block and select it
        const single = rightContent.firstChild!;
        tr = tr.insert(insertPos, copyNode(single));
        tr = selectAtBlockStart(tr, insertPos, single); // caret → the moved right element
        dispatch(tr.scrollIntoView());
        return true;
      } else {
        // Insert a brand-new row for the right side and select its first child
        const rightRow = buildRow(rightContent);
        tr = tr.insert(insertPos, rightRow);
        const rowStartPos = insertPos; // the newly inserted right row starts at insertPos
        const firstRight = rightRow.firstChild!;
        tr = selectAtBlockStart(tr, rowStartPos + 1, firstRight); // caret → first child of right row
        dispatch(tr.scrollIntoView());
        return true;
      }
    }

    // --- Selected is NOT the first (but also not last) ---
    const leftRow = buildRow(leftContent);

    // Replace original row with the left row
    tr = tr.replaceRangeWith(rowInfo.pos, rowInfo.pos + rowInfo.node.nodeSize, leftRow);
    const mappedLeftPos = tr.mapping.map(rowInfo.pos);
    const insertAfterLeft = mappedLeftPos + leftRow.nodeSize;

    if (rightCount === 1) {
      // Move the single right element outside, place it after the left row, and select it
      const single = rightContent.firstChild!;
      tr = tr.insert(insertAfterLeft, copyNode(single));
      tr = selectAtBlockStart(tr, insertAfterLeft, single); // caret → the moved right element
      dispatch(tr.scrollIntoView());
      return true;
    }

    // Default: 2+ on the right → brand-new right row after left row; select its first child
    const rightRow = buildRow(rightContent);
    tr = tr.insert(insertAfterLeft, rightRow);
    const rightRowStart = insertAfterLeft; // start of inserted row
    const firstRight = rightRow.firstChild!;
    tr = selectAtBlockStart(tr, rightRowStart + 1, firstRight); // caret → first child of right row

    dispatch(tr.scrollIntoView());
    return true;
  };
}
